import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Button,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SpeedIcon from '@mui/icons-material/Speed';
import ReplyIcon from '@mui/icons-material/Reply';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import facturaVentaService, {
  FacturaVenta,
  FacturaVentaEnvioEmailPreview,
  FacturaVentaEnvioEmailRequest,
  resolveDetalleMasterKeys,
  resolveMasterNumber,
} from '../../services/facturaVentaService';
import sucVentaService from '../../services/sucVentaService';
import remitoVentaService, { RemitoVentaCreateDto } from '../../services/remitoVentaService';
import ventasSeguimientoService from '../../services/ventasSeguimientoService';
import { resolverPrecioListaDetalle } from '../../utils/normalizarDetallesFacturaVenta';
import mercadoPagoService from '../../services/mercadoPagoService';
import PDFViewer from '../../components/shared/PDFViewer';
import QRPaymentDialog from '../../components/shared/QRPaymentDialog';
import EnviarFacturaEmailDialog from '../../components/ventas/EnviarFacturaEmailDialog';
import EditarObservacionesFacturaDialog from '../../components/ventas/EditarObservacionesFacturaDialog';
import { usePosTicketPrinter } from '../../hooks/usePosTicketPrinter';
import {
  cargarMapaSucVentaPorClave,
  cargarMapaUbicacionNroSuc,
  facturaInputDesdeFacturaVenta,
  resolverContextoSucVentaImpresion,
  resolverModoImpresionFactura,
  resolverSucVentaFactura,
  type SucVentaMapEntry,
} from '../../utils/sucVentaImpresionResolver';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { useRef } from 'react';
import { ROUTES, facturaPosPath, facturaPosVerPath, facturaPosEditarPath } from '../../config/routes';

type RemitoStatus = 'unknown' | 'none' | 'full' | 'partial';
type RemitoDraftItem = {
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  codigoArticulo: string;
  descripcionArticulo: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  notas?: string;
};

const Facturas: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro({
    urlFechaDesde: searchParams.get('fechaDesde'),
    urlFechaHasta: searchParams.get('fechaHasta'),
  });

  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } = useSucursalesVentaFiltro();
  const facturaPrinter = usePosTicketPrinter(0);
  const [printingFacturaId, setPrintingFacturaId] = useState<number | null>(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState<{ el: HTMLElement; factura: FacturaVenta } | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [selectedFacturaForPayment, setSelectedFacturaForPayment] = useState<number | null>(null);
  const [paymentLinkIsPrototype, setPaymentLinkIsPrototype] = useState(false);
  const [obteniendoCae, setObteniendoCae] = useState<number | null>(null);
  const [sucVentaMap, setSucVentaMap] = useState<Record<string, SucVentaMapEntry>>({});
  const [ubicacionNroSucMap, setUbicacionNroSucMap] = useState<Record<number, number>>({});
  const [generandoRemito, setGenerandoRemito] = useState<number | null>(null);
  const [remitoStatusMap, setRemitoStatusMap] = useState<Record<number, RemitoStatus>>({});
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; facturaId: number } | null>(null);
  const [remitoActionsAnchor, setRemitoActionsAnchor] = useState<{ el: HTMLElement; facturaId: number } | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState<FacturaVentaEnvioEmailPreview | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [selectedFacturaForEmail, setSelectedFacturaForEmail] = useState<number | null>(null);
  const [observacionesDialogOpen, setObservacionesDialogOpen] = useState(false);
  const [selectedFacturaForObservaciones, setSelectedFacturaForObservaciones] = useState<number | null>(null);
  const [observacionesIniciales, setObservacionesIniciales] = useState('');
  const [observacionesComprobanteLabel, setObservacionesComprobanteLabel] = useState<string | null>(null);
  const [observacionesLoading, setObservacionesLoading] = useState(false);
  const [observacionesSaving, setObservacionesSaving] = useState(false);
  const facturaObservacionesRef = useRef<FacturaVenta | null>(null);
  const remitoStatusCacheRef = useRef<Map<number, RemitoStatus>>(new Map());
  const remitoStatusPendingRef = useRef<Map<number, Promise<RemitoStatus>>>(new Map());

  const mapEstadoFacturaParam = (v: string): string | undefined => {
    if (!v) return undefined;
    if (v === 'conCae') return 'ConCae';
    if (v === 'sinCae') return 'SinCae';
    if (v === 'interno') return 'Interno';
    return undefined;
  };

  const fetchFacturas = useCallback(async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    estado: string,
    nroSuc: number | null
  ) => {
    try {
      setLoading(true);
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await facturaVentaService.getFacturasVenta({
        pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
        estadoFactura: mapEstadoFacturaParam(estado),
        nroSuc: resolveNroSucFiltro(nroSuc),
      });

      setFacturas(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      console.error('Error al cargar facturas:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al cargar las facturas');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000
      });
      setFacturas([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Sincronizar fechas desde URL cuando cambian los searchParams (ej. al llegar desde dashboard)
  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) setFechaDesde(desde);
    if (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) setFechaHasta(hasta);
    setPage(0);
  }, [searchParams, setFechaDesde, setFechaHasta]);

  useEffect(() => {
    fetchFacturas(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc, fetchFacturas]);

  useEffect(() => {
    const loadImpresionMaps = async () => {
      try {
        const [sucVentaPorClave, ubicacionPorNro] = await Promise.all([
          cargarMapaSucVentaPorClave(),
          cargarMapaUbicacionNroSuc(),
        ]);
        setSucVentaMap(sucVentaPorClave);
        setUbicacionNroSucMap(ubicacionPorNro);
      } catch (err) {
        console.warn('No se pudieron cargar SucVentas / ubicaciones para impresión:', err);
      }
    };
    loadImpresionMaps();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validRowsPerPage = Math.max(1, Math.min(20, parseInt(event.target.value, 10)));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const closePrintMenu = () => setPrintMenuAnchor(null);

  const modoImpresionFactura = useCallback(
    (factura: FacturaVenta) =>
      resolverModoImpresionFactura(
        facturaInputDesdeFacturaVenta(factura),
        sucVentaMap,
        ubicacionNroSucMap,
      ),
    [sucVentaMap, ubicacionNroSucMap],
  );

  const facturaPermiteImpresion = useCallback(
    (factura: FacturaVenta) => modoImpresionFactura(factura) !== null,
    [modoImpresionFactura],
  );

  const handleImprimirFactura = (event: React.MouseEvent<HTMLElement>, factura: FacturaVenta) => {
    const modo = modoImpresionFactura(factura);
    if (modo === 'ticketera') {
      openPrintMenu(event, factura);
      return;
    }
    if (modo === 'pdf') {
      void ejecutarImpresionFactura(factura, 'pdf');
    }
  };

  const openPrintMenu = (event: React.MouseEvent<HTMLElement>, factura: FacturaVenta) => {
    setPrintMenuAnchor({ el: event.currentTarget, factura });
  };

  const ejecutarImpresionFactura = async (
    factura: FacturaVenta,
    modo: 'pdf' | 'ticket',
  ) => {
    closePrintMenu();
    setPrintingFacturaId(factura.nroFormulario);
    try {
      await facturaPrinter.imprimirFactura(
        {
          nroFormulario: factura.nroFormulario,
          nroSuc: factura.nroSuc,
          nroTipoFormulario: factura.nroTipoFormulario,
          nroUbicacionOrigen: factura.nroUbicacionOrigen,
          ubicacion: factura.ubicacion,
          letra: factura.letra,
        },
        modo === 'pdf' ? { forzarPdf: true } : { forzarTicket: true },
      );
    } finally {
      setPrintingFacturaId(null);
    }
  };

  const getErrorMessage = (error: any, defaultMessage: string) => {
    if (error?.response?.data) {
      let data = error.response.data;

      if (typeof data === 'string') {
        try {
          if (data.trim().startsWith('{') || data.trim().startsWith('[')) {
            data = JSON.parse(data);
          } else {
            return data;
          }
        } catch {
          return data;
        }
      }

      if (typeof data === 'object' && data !== null) {
        const mensaje =
          data.mensaje ||
          data.message ||
          data.error ||
          data.detail ||
          data.title ||
          data.Mensaje ||
          data.Message;

        if (mensaje) {
          return mensaje;
        }

        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors[0].message || data.errors[0].mensaje || data.errors[0];
        }
      }
    }

    return error?.message || defaultMessage;
  };

  const buildDemoPaymentLink = (nroFormulario: number) =>
    `https://www.mercadopago.com.ar/checkout/v1/redirect?pref_id=PROTOTIPO-FAC-${nroFormulario}`;

  const applyDemoPaymentLink = (nroFormulario: number) => {
    const demoLink = buildDemoPaymentLink(nroFormulario);
    setQrCode(demoLink);
    setPaymentLink(demoLink);
    setPaymentLinkIsPrototype(true);
  };

  const handleGenerarLinkPago = async (nroFormulario: number) => {
    setSelectedFacturaForPayment(nroFormulario);
    setQrDialogOpen(true);
    setQrCode(null);
    setPaymentLink(null);
    setPaymentLinkIsPrototype(false);
    setQrLoading(true);

    try {
      const response = await mercadoPagoService.getPaymentLink(nroFormulario);
      const link = response.paymentLink?.trim();
      if (!link) {
        applyDemoPaymentLink(nroFormulario);
        enqueueSnackbar('Link de demostración (Mercado Pago aún no disponible para esta factura).', {
          variant: 'info',
          autoHideDuration: 4000,
        });
        return;
      }
      setQrCode(response.qrCode || link);
      setPaymentLink(link);
    } catch (error: unknown) {
      console.error('Error al generar link de pago:', error);
      applyDemoPaymentLink(nroFormulario);
      enqueueSnackbar('No se pudo generar el link real. Se muestra un QR de demostración.', {
        variant: 'info',
        autoHideDuration: 4000,
      });
    } finally {
      setQrLoading(false);
    }
  };

  const handleCloseQRDialog = () => {
    setQrDialogOpen(false);
    setQrCode(null);
    setPaymentLink(null);
    setSelectedFacturaForPayment(null);
    setPaymentLinkIsPrototype(false);
  };

  const handleAbrirEnvioEmail = async (nroFormulario: number) => {
    setSelectedFacturaForEmail(nroFormulario);
    setEmailDialogOpen(true);
    setEmailPreview(null);
    setEmailPreviewLoading(true);

    try {
      const preview = await facturaVentaService.getEnvioEmailPreview(nroFormulario);
      setEmailPreview(preview);
    } catch (error: unknown) {
      console.error('Error al cargar datos de envío por mail:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar los datos para el envío por mail'), {
        variant: 'error',
      });
      setEmailDialogOpen(false);
      setSelectedFacturaForEmail(null);
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailPreview(null);
    setSelectedFacturaForEmail(null);
  };

  const handleAbrirEditarObservaciones = async (factura: FacturaVenta) => {
    const comprobante = formatComprobante(factura);
    facturaObservacionesRef.current = null;
    setSelectedFacturaForObservaciones(factura.nroFormulario);
    setObservacionesComprobanteLabel(comprobante !== '-' ? comprobante : null);
    setObservacionesIniciales(String(factura.descripcion ?? '').trim());
    setObservacionesDialogOpen(true);
    setObservacionesLoading(true);

    try {
      const detalle = await facturaVentaService.getFacturaVenta(factura.nroFormulario);
      facturaObservacionesRef.current = detalle;
      setObservacionesIniciales(String(detalle.descripcion ?? '').trim());
    } catch (error: unknown) {
      console.error('Error al cargar observaciones de la factura:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar las observaciones'), {
        variant: 'error',
      });
      setObservacionesDialogOpen(false);
      setSelectedFacturaForObservaciones(null);
      facturaObservacionesRef.current = null;
    } finally {
      setObservacionesLoading(false);
    }
  };

  const handleCloseObservacionesDialog = () => {
    setObservacionesDialogOpen(false);
    setSelectedFacturaForObservaciones(null);
    setObservacionesIniciales('');
    setObservacionesComprobanteLabel(null);
    facturaObservacionesRef.current = null;
  };

  const handleGuardarObservaciones = async (observaciones: string) => {
    if (!selectedFacturaForObservaciones) return;

    const descripcion = observaciones.trim();
    const actual = String(
      facturaObservacionesRef.current?.descripcion ?? observacionesIniciales,
    ).trim();
    if (descripcion === actual) {
      handleCloseObservacionesDialog();
      return;
    }

    try {
      setObservacionesSaving(true);
      await facturaVentaService.updateFacturaVentaObservaciones(
        selectedFacturaForObservaciones,
        descripcion,
        facturaObservacionesRef.current ?? undefined,
      );
      setFacturas((prev) =>
        prev.map((f) =>
          f.nroFormulario === selectedFacturaForObservaciones ? { ...f, descripcion } : f,
        ),
      );
      enqueueSnackbar('Observaciones actualizadas correctamente', { variant: 'success' });
      handleCloseObservacionesDialog();
    } catch (error: unknown) {
      console.error('Error al guardar observaciones:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron guardar las observaciones'), {
        variant: 'error',
      });
    } finally {
      setObservacionesSaving(false);
    }
  };

  const handleEnviarFacturaEmail = async (payload: FacturaVentaEnvioEmailRequest) => {
    if (!selectedFacturaForEmail) return;

    try {
      setEmailSending(true);
      const result = await facturaVentaService.enviarFacturaPorEmail(selectedFacturaForEmail, payload);
      enqueueSnackbar(result.mensaje ?? 'Factura enviada por correo correctamente', { variant: 'success' });
      handleCloseEmailDialog();
    } catch (error: unknown) {
      console.error('Error al enviar factura por mail:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo enviar la factura por correo'), {
        variant: 'error',
      });
    } finally {
      setEmailSending(false);
    }
  };

  const handleObtenerCae = async (nroFormulario: number) => {
    try {
      setObteniendoCae(nroFormulario);
      const facturaActualizada = await facturaVentaService.obtenerCaeArgentina(nroFormulario);
      
      // Actualizar la factura en la lista con todos los campos relevantes
      setFacturas(prevFacturas => 
        prevFacturas.map(f => 
          f.nroFormulario === nroFormulario 
            ? { 
                ...f, 
                nroComprobante: facturaActualizada.nroComprobante,
                caeNro: facturaActualizada.caeNro, 
                caeVencimiento: facturaActualizada.caeVencimiento,
                caeResultado: facturaActualizada.caeResultado,
                caeObs: facturaActualizada.caeObs
              }
            : f
        )
      );
      
      enqueueSnackbar('CAE obtenido exitosamente', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al obtener CAE:', error);
      const errorMessage = getErrorMessage(error, 'Error al obtener el CAE');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 8000
      });
    } finally {
      setObteniendoCae(null);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      return date.toLocaleDateString('es-AR');
    } catch {
      return typeof dateString === 'string' ? dateString : '-';
    }
  };

  const formatComprobante = (factura: FacturaVenta) => {
    const { letra, nroSuc, nroComprobante } = factura;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  /** Facturas internas (tipo IVA N o letra X): no requieren CAE AFIP. */
  const esFacturaInterna = (factura: FacturaVenta) => {
    const tipoIva = (factura.tipoIva ?? '').toString().trim().toUpperCase();
    const letra = (factura.letra ?? '').toString().trim().toUpperCase();
    return tipoIva === 'N' || letra === 'X';
  };

  const esFacturaNoCategorizada = (factura: FacturaVenta) => esFacturaInterna(factura);

  const handleFacturarFacturaNoCategorizada = (nroFormulario: number) => {
    if (!nroFormulario) return;
    navigate(facturaPosPath({ facturaId: nroFormulario }));
  };

  const handleAnular = async (facturaId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (window.confirm('¿Está seguro que desea anular esta factura?')) {
      try {
        await facturaVentaService.anularFactura(facturaId);
        enqueueSnackbar('Factura anulada correctamente', { variant: 'success' });
        fetchFacturas(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc);
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.response?.data || 'Error al anular la factura';
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const puedeGenerarRemitoPorSucursal = (factura: FacturaVenta) => {
    if (factura.nroFormularioRemito != null && factura.nroFormularioRemito > 0) return false;
    const sv = resolverSucVentaFactura(
      facturaInputDesdeFacturaVenta(factura),
      sucVentaMap,
      ubicacionNroSucMap,
    );
    return sv?.generaRemito === 'S';
  };

  const setRemitoStatus = (nroFormulario: number, status: RemitoStatus) => {
    remitoStatusCacheRef.current.set(nroFormulario, status);
    setRemitoStatusMap((prev) => ({ ...prev, [nroFormulario]: status }));
  };

  const getRemitoStatus = useCallback(async (nroFormulario: number, force = false): Promise<RemitoStatus> => {
    if (!force) {
      const cached = remitoStatusCacheRef.current.get(nroFormulario);
      if (cached) return cached;
      const pending = remitoStatusPendingRef.current.get(nroFormulario);
      if (pending) return pending;
    }

    const promise = (async (): Promise<RemitoStatus> => {
      try {
        const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
        if (!puedeGenerarRemitoPorSucursal(factura)) {
          setRemitoStatus(nroFormulario, 'none');
          return 'none';
        }
        if (!factura.detalles || factura.detalles.length === 0) {
          setRemitoStatus(nroFormulario, 'none');
          return 'none';
        }

        // Si alguna línea apunta a un master distinto de la factura (ej. remito 996816 → factura 996817),
        // no usar fallback al nroFormulario de la factura: solo masters explícitos del ítem.
        const tieneMasterExterno = factura.detalles.some((d) => {
          const mf = resolveMasterNumber(d as any, [
            'nroFormularioMaster',
            'nroformulariomaster',
            'NroFormularioMaster',
            'nro_formulario_master',
          ]);
          return mf != null && mf > 0 && mf !== nroFormulario;
        });

        const keys = new Map<string, { masterFormulario: number; masterItem: number }>();
        factura.detalles.forEach((d, index) => {
          const master = tieneMasterExterno
            ? resolveDetalleMasterKeys(d)
            : resolveDetalleMasterKeys(d, {
                fallbackFormulario: nroFormulario,
                fallbackItem: Number((d as any).nroItem ?? index + 1),
              });
          if (!master) return;
          const key = `${master.nroFormularioMaster}-${master.nroItemMaster}`;
          if (!keys.has(key)) {
            keys.set(key, {
              masterFormulario: master.nroFormularioMaster,
              masterItem: master.nroItemMaster,
            });
          }
        });

        if (keys.size === 0) {
          setRemitoStatus(nroFormulario, 'none');
          return 'none';
        }

        const hilos = await Promise.all(
          Array.from(keys.values()).map((k) =>
            ventasSeguimientoService.getHilo(k.masterFormulario, k.masterItem)
          )
        );

        let pendienteTotal = 0;
        let tieneRemitosPrevios = false;
        let facturaConRemitoPrevio = false;
        hilos.forEach((h) => {
          const comprobantes = h.comprobantes || [];
          const cantidades = comprobantes.reduce(
            (acc, c) => {
              const tipo = String(c.nroTipoFormulario ?? '').trim();
              const cantidad = Number(c.cantidad ?? 0);
              if (tipo === '1' || tipo === '2') acc.facturada += cantidad;
              if (tipo === '3') acc.devuelta += cantidad;
              if (tipo === '9') acc.remitida += cantidad;
              return acc;
            },
            { facturada: 0, devuelta: 0, remitida: 0 }
          );
          const cantidadFacturadaNeta = cantidades.facturada - cantidades.devuelta;
          const pendienteEntregar = Math.max(0, cantidadFacturadaNeta - cantidades.remitida);
          pendienteTotal += pendienteEntregar;
          if (cantidades.remitida > 0) {
            tieneRemitosPrevios = true;
          }
          const hayRemitoEnHilo = comprobantes.some((c) => String(c.nroTipoFormulario ?? '').trim() === '9');
          const estaFacturaEnHilo = comprobantes.some((c) => c.nroFormulario === nroFormulario);
          if (hayRemitoEnHilo && estaFacturaEnHilo && pendienteEntregar <= 0) {
            facturaConRemitoPrevio = true;
          }
        });

        let status: RemitoStatus;
        if (pendienteTotal <= 0 || facturaConRemitoPrevio) {
          status = 'none';
        } else if (tieneRemitosPrevios) {
          status = 'partial';
        } else {
          status = 'full';
        }
        setRemitoStatus(nroFormulario, status);
        return status;
      } catch (error) {
        console.warn(`No se pudo calcular estado de remito para factura ${nroFormulario}:`, error);
        setRemitoStatus(nroFormulario, 'unknown');
        return 'unknown';
      } finally {
        remitoStatusPendingRef.current.delete(nroFormulario);
      }
    })();

    remitoStatusPendingRef.current.set(nroFormulario, promise);
    return promise;
  }, [sucVentaMap]);

  const invalidateRemitoStatus = (nroFormulario: number) => {
    remitoStatusCacheRef.current.delete(nroFormulario);
    remitoStatusPendingRef.current.delete(nroFormulario);
    setRemitoStatusMap((prev) => {
      const next = { ...prev };
      delete next[nroFormulario];
      return next;
    });
  };

  const resolveDetalleMaster = (detalle: any, index: number, nroFormularioDefault: number) => {
    const master = resolveDetalleMasterKeys(detalle, {
      fallbackFormulario: nroFormularioDefault,
      fallbackItem: Number((detalle as any).nroItem ?? index + 1),
    });
    if (master) return master;
    return {
      nroFormularioMaster: nroFormularioDefault,
      nroItemMaster: Number((detalle as any).nroItem ?? index + 1),
    };
  };

  const buildRemitoParcialDraft = async (nroFormulario: number): Promise<{
    nroFormularioFactura: number;
    formulario: {
      nroCliente: number;
      nombre?: string;
      cuit?: string;
      nroLista?: string;
      nroSuc: number;
      descripcion?: string;
      nroUbicacionOrigen?: number;
      porcDesc?: number | null;
      porcDesc2?: number | null;
      porcDesc3?: number | null;
    };
    items: RemitoDraftItem[];
  }> => {
    const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
    const ctx = resolverContextoSucVentaImpresion(
      facturaInputDesdeFacturaVenta(factura),
      ubicacionNroSucMap,
      sucVentaMap,
      'cualquiera',
    );
    const sucVenta = await sucVentaService.getSucVenta(ctx.nroSuc, ctx.nroTipoFormulario);

    if (sucVenta.generaRemito !== 'S') {
      throw new Error('Esta sucursal no permite generar remitos desde facturas');
    }
    if (!factura.nroCliente || factura.nroCliente <= 0) {
      throw new Error('La factura debe tener un cliente asignado');
    }
    if (!factura.detalles || factura.detalles.length === 0) {
      throw new Error('La factura no tiene items para generar remito');
    }

    const hiloCache = new Map<string, Awaited<ReturnType<typeof ventasSeguimientoService.getHilo>>>();
    const pendingItems: RemitoDraftItem[] = [];

    for (let index = 0; index < factura.detalles.length; index++) {
      const d = factura.detalles[index];
      const { nroFormularioMaster, nroItemMaster } = resolveDetalleMaster(d, index, nroFormulario);
      const key = `${nroFormularioMaster}-${nroItemMaster}`;
      if (!hiloCache.has(key)) {
        const hilo = await ventasSeguimientoService.getHilo(nroFormularioMaster, nroItemMaster);
        hiloCache.set(key, hilo);
      }
      const hilo = hiloCache.get(key);
      const cantidades = (hilo?.comprobantes || []).reduce(
        (acc, c) => {
          const tipo = String(c.nroTipoFormulario ?? '').trim();
          const cantidad = Number(c.cantidad ?? 0);
          if (tipo === '1' || tipo === '2') acc.facturada += cantidad;
          if (tipo === '3') acc.devuelta += cantidad;
          if (tipo === '9') acc.remitida += cantidad;
          return acc;
        },
        { facturada: 0, devuelta: 0, remitida: 0 }
      );
      const pendienteEntregar = Math.max(0, (cantidades.facturada - cantidades.devuelta) - cantidades.remitida);
      if (pendienteEntregar <= 0) continue;

      pendingItems.push({
        nroFormularioMaster,
        nroItemMaster,
        codigoArticulo: String(d.codigoArticulo ?? '').trim(),
        descripcionArticulo: d.descripcionArticulo ?? '',
        cantidad: Number(pendienteEntregar),
        precioUnitario: resolverPrecioListaDetalle(d),
        porcDesc: Number(d.porcDesc ?? 0),
        notas: d.notas ?? '',
      });
    }

    return {
      nroFormularioFactura: nroFormulario,
      formulario: {
        nroCliente: factura.nroCliente,
        nombre: factura.nombre,
        cuit: factura.cuit,
        nroLista: factura.nroLista,
        nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
        descripcion: factura.descripcion ?? `Remito parcial desde factura ${nroFormulario}`,
        nroUbicacionOrigen: (factura as any).nroUbicacionOrigen ?? undefined,
        porcDesc: factura.porcDesc ?? 0,
        porcDesc2: factura.porcDesc2 ?? 0,
        porcDesc3: factura.porcDesc3 ?? 0,
      },
      items: pendingItems,
    };
  };

  const getRemitoButtonState = (factura: FacturaVenta) => {
    const status = remitoStatusMap[factura.nroFormulario] ?? 'unknown';
    if (status === 'partial') {
      return { show: true, title: 'Generar remito parcial', bg: '#f57c00', hover: '#ef6c00' };
    }
    if (status === 'full') {
      return { show: true, title: 'Generar remito', bg: '#795548', hover: '#6d4c41' };
    }
    return { show: false, title: 'Generar remito', bg: '#795548', hover: '#6d4c41' };
  };

  useEffect(() => {
    const visibles = facturas.filter((f) => puedeGenerarRemitoPorSucursal(f));
    visibles.forEach((f) => {
      if (!remitoStatusCacheRef.current.has(f.nroFormulario) && !remitoStatusPendingRef.current.has(f.nroFormulario)) {
        void getRemitoStatus(f.nroFormulario);
      }
    });
  }, [facturas, getRemitoStatus, sucVentaMap, ubicacionNroSucMap]);

  const handleGenerarRemito = async (nroFormulario: number) => {
    try {
      setGenerandoRemito(nroFormulario);
      const estadoRemito = await getRemitoStatus(nroFormulario);
      if (estadoRemito === 'none') {
        enqueueSnackbar('La factura no tiene cantidades pendientes de remitir', { variant: 'error' });
        return;
      }
      const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
      const ctx = resolverContextoSucVentaImpresion(
        facturaInputDesdeFacturaVenta(factura),
        ubicacionNroSucMap,
        sucVentaMap,
        'cualquiera',
      );
      const sucVenta = await sucVentaService.getSucVenta(ctx.nroSuc, ctx.nroTipoFormulario);
      if (sucVenta.generaRemito !== 'S') {
        enqueueSnackbar('Esta sucursal no permite generar remitos desde facturas', { variant: 'error' });
        return;
      }
      if (!factura.detalles || factura.detalles.length === 0) {
        enqueueSnackbar('La factura no tiene items para generar el remito', { variant: 'error' });
        return;
      }
      if (!factura.nroCliente || factura.nroCliente <= 0) {
        enqueueSnackbar('La factura debe tener un cliente asignado', { variant: 'error' });
        return;
      }
      const ahora = new Date();
      const payload: RemitoVentaCreateDto = {
        formulario: {
          nroCliente: factura.nroCliente,
          nombre: factura.nombre,
          cuit: factura.cuit,
          nroLista: factura.nroLista,
          nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
          descripcion: factura.descripcion ?? `Remito desde factura ${nroFormulario}`,
          nroFormularioFactura: nroFormulario,
          fechaEmision: ahora.toISOString(),
          horaEmision: ahora.toISOString(),
          fechaVencim: ahora.toISOString(),
          nroUbicacionOrigen: (factura as any).nroUbicacionOrigen ?? undefined,
          porcDesc: factura.porcDesc ?? 0,
          porcDesc2: factura.porcDesc2 ?? 0,
          porcDesc3: factura.porcDesc3 ?? 0,
        },
        items: factura.detalles.map((d) => {
          const nroFormularioMaster = resolveMasterNumber(d as any, [
            'nroFormularioMaster',
            'nroformulariomaster',
            'NroFormularioMaster',
            'nro_formulario_master',
          ]);
          const nroItemMaster = resolveMasterNumber(d as any, [
            'nroItemMaster',
            'nroitemmaster',
            'NroItemMaster',
            'nro_item_master',
          ]);
          return {
            codigoArticulo: String(d.codigoArticulo ?? '').trim(),
            descripcion: d.descripcionArticulo ?? '',
            cantidad: Number(d.cantidad ?? 0),
            precioUnitario: resolverPrecioListaDetalle(d),
            porcDesc: d.porcDesc ?? 0,
            notas: d.notas ?? '',
            nroFormularioMaster,
            nroItemMaster,
          };
        }),
      };
      const remitoCreado = await remitoVentaService.createRemitoVenta(payload);
      enqueueSnackbar(`Remito generado correctamente (Nro: ${remitoCreado.nroFormulario})`, { variant: 'success' });
      invalidateRemitoStatus(nroFormulario);
      void getRemitoStatus(nroFormulario, true);
      navigate('/ventas/remitos');
    } catch (error: any) {
      const msg = getApiErrorMessage(error, 'Error al generar el remito');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setGenerandoRemito(null);
    }
  };

  const handleAbrirRemitoParcial = async (nroFormulario: number) => {
    try {
      setGenerandoRemito(nroFormulario);
      const estadoRemito = await getRemitoStatus(nroFormulario);
      if (estadoRemito === 'none') {
        enqueueSnackbar('La factura no tiene cantidades pendientes de remitir', { variant: 'error' });
        return;
      }

      const draft = await buildRemitoParcialDraft(nroFormulario);
      if (!draft.items.length) {
        enqueueSnackbar('No hay items con pendiente para generar remito parcial', { variant: 'warning' });
        return;
      }

      navigate('/ventas/remitos/nuevo', {
        state: {
          remitoParcialDesdeFactura: draft,
        },
      });
    } catch (error: any) {
      const msg = getApiErrorMessage(error, 'Error al preparar el remito parcial');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setGenerandoRemito(null);
    }
  };

  const openRemitoActions = (event: React.MouseEvent<HTMLElement>, facturaId: number) => {
    event.stopPropagation();
    setRemitoActionsAnchor({ el: event.currentTarget, facturaId });
  };

  const closeRemitoActions = () => setRemitoActionsAnchor(null);

  const getEstadoChip = (factura: FacturaVenta) => {
    if (esFacturaInterna(factura)) {
      return { label: 'Interno', bgColor: '#ECEFF1', textColor: '#455A64' };
    }
    if (factura.caeNro) {
      return { label: 'Con CAE', bgColor: '#C8E6C9', textColor: '#2E7D32' };
    }
    return { label: 'Sin CAE', bgColor: '#FFE0B2', textColor: '#E65100' };
  };

  const handleAbrirFacturaPos = (nroFormulario: number) => {
    navigate(facturaPosVerPath(nroFormulario));
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, facturaId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, facturaId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const puedeEditarFactura =
    Boolean(user?.roles.includes('SUPERADMIN') || user?.roles.includes('BACKDOOR') || user?.roles.includes('SOPORTE-IT'));

  const btnSx = (bgColor: string, hoverColor: string) => ({
    bgcolor: bgColor,
    color: '#fff',
    borderRadius: 1.5,
    width: 32,
    height: 32,
    '&:hover': { bgcolor: hoverColor, transform: 'scale(1.1)' },
    '&:disabled': { bgcolor: '#ccc' },
    transition: 'all 0.2s',
  });

  const currentKebabFactura = kebabAnchor
    ? facturas.find((f) => f.nroFormulario === kebabAnchor.facturaId)
    : null;

  const renderKebabFacturaItems = (factura: FacturaVenta) => {
    const items: React.ReactElement[] = [];
    if (esFacturaNoCategorizada(factura)) {
      items.push(
        <MenuItem key="facturar" onClick={() => { handleKebabClose(); handleFacturarFacturaNoCategorizada(factura.nroFormulario); }}>
          <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Facturar con CAE</ListItemText>
        </MenuItem>
      );
    }
    if (puedeEditarFactura) {
      items.push(
        <MenuItem key="editar" onClick={() => { handleKebabClose(); navigate(facturaPosEditarPath(factura.nroFormulario)); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
      );
    }
    items.push(
      <MenuItem
        key="observaciones"
        onClick={() => {
          handleKebabClose();
          void handleAbrirEditarObservaciones(factura);
        }}
      >
        <ListItemIcon><NotesIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Editar observaciones</ListItemText>
      </MenuItem>
    );
    items.push(
      <MenuItem key="nc-pos" onClick={() => { handleKebabClose(); navigate(`/ventas/notas-credito/nuevo-pos?facturaId=${factura.nroFormulario}`); }}>
        <ListItemIcon><ReplyIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Nota de crédito</ListItemText>
      </MenuItem>
    );
    if (!factura.caeNro) {
      items.push(
        <MenuItem key="anular" onClick={() => { handleKebabClose(); void handleAnular(factura.nroFormulario); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Anular factura</ListItemText>
        </MenuItem>
      );
    }
    items.push(
      <MenuItem key="email" onClick={() => { handleKebabClose(); void handleAbrirEnvioEmail(factura.nroFormulario); }}>
        <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Enviar por mail</ListItemText>
      </MenuItem>
    );
    items.push(
      <MenuItem key="mp" onClick={() => { handleKebabClose(); void handleGenerarLinkPago(factura.nroFormulario); }}>
        <ListItemIcon><QrCodeIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Link de pago (Mercado Pago)</ListItemText>
      </MenuItem>
    );
    return items;
  };

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Facturas de Venta
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={() => navigate(ROUTES.ventas.facturas.pos)}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' }
                }}
              >
                Nueva Factura POS
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número, cliente, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Estado"
                  allowEmpty
                  emptyOptionLabel="Todos"
                  value={filtroEstado || null}
                  onChange={(v) => { setFiltroEstado(v ?? ''); setPage(0); }}
                  options={[
                    { value: 'conCae', label: 'Con CAE' },
                    { value: 'sinCae', label: 'Sin CAE' },
                    { value: 'interno', label: 'Interno' },
                  ]}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {facturas.map((factura) => {
                const estadoChip = getEstadoChip(factura);
                const comprobante = formatComprobante(factura);
                return (
                  <Card 
                    key={factura.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              component="span"
                              variant="subtitle1"
                              sx={{ ...comprobanteSx, fontWeight: 600, display: 'block' }}
                              onClick={() => handleAbrirFacturaPos(factura.nroFormulario)}
                            >
                              {comprobante !== '-' ? comprobante : `Factura #${factura.nroFormulario}`}
                            </Typography>
                          </Box>
                          {estadoChip.label ? (
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 500,
                                borderRadius: 1.5,
                              }}
                            />
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(factura.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {factura.nombre?.trim() || '-'}
                        </Typography>
                        {factura.cuit?.trim() ? (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {factura.cuit.trim()}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe Total:</strong> {formatCurrency(factura.importeTotal)}
                        </Typography>
                        {factura.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {factura.usuario}
                          </Typography>
                        )}
                        {factura.caeNro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {factura.caeNro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                          {!factura.caeNro && !esFacturaInterna(factura) && (
                            <Tooltip title="Obtener CAE de Argentina">
                              <IconButton
                                onClick={() => handleObtenerCae(factura.nroFormulario)}
                                size="small"
                                disabled={obteniendoCae === factura.nroFormulario}
                                sx={btnSx('#2196f3', '#1976d2')}
                              >
                                {obteniendoCae === factura.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {(() => {
                            const remitoBtn = getRemitoButtonState(factura);
                            if (!remitoBtn.show) return null;
                            return (
                            <Tooltip title={remitoBtn.title}>
                              <IconButton
                                onMouseEnter={() => { void getRemitoStatus(factura.nroFormulario); }}
                                onClick={(e) => openRemitoActions(e, factura.nroFormulario)}
                                size="small"
                                disabled={generandoRemito === factura.nroFormulario}
                                sx={btnSx(remitoBtn.bg, remitoBtn.hover)}
                              >
                                {generandoRemito === factura.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <LocalShippingIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            );
                          })()}
                          {facturaPermiteImpresion(factura) && (
                          <Tooltip title="Imprimir">
                            <IconButton
                              onClick={(e) => handleImprimirFactura(e, factura)}
                              size="small"
                              disabled={printingFacturaId === factura.nroFormulario || facturaPrinter.printing}
                              sx={btnSx('#2196f3', '#1976d2')}
                            >
                              {printingFacturaId === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          )}
                          <Tooltip title="Más acciones">
                            <IconButton
                              size="small"
                              onClick={(e) => handleKebabOpen(e, factura.nroFormulario)}
                              sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && facturas.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay facturas para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%'
                }
              }}
            >
          <Table
            size="medium"
            sx={{
              minWidth: 720,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                padding: { xs: '8px 4px', sm: '10px 8px', md: '12px 8px' }
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nro. Factura</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facturas.map((factura) => {
                const estadoChip = getEstadoChip(factura);
                return (
                  <TableRow 
                    key={factura.nroFormulario} 
                    sx={{
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{factura.nroFormulario}</TableCell>
                    <TableCell>{formatDate(factura.fechaEmision)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                      <Typography component="span" sx={comprobanteSx} onClick={() => handleAbrirFacturaPos(factura.nroFormulario)}>
                        {formatComprobante(factura)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{factura.nombre?.trim() || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>{formatCurrency(factura.importeTotal)}</TableCell>
                    <TableCell>
                      {estadoChip.label ? (
                        <Chip
                          label={estadoChip.label}
                          size="small"
                          sx={{
                            bgcolor: estadoChip.bgColor,
                            color: estadoChip.textColor,
                            fontWeight: 500,
                            borderRadius: 1.5,
                            minWidth: 90,
                          }}
                        />
                      ) : ''}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                        {!factura.caeNro && !esFacturaInterna(factura) && (
                          <Tooltip title="Obtener CAE de Argentina">
                            <IconButton
                              onClick={() => handleObtenerCae(factura.nroFormulario)}
                              size="small"
                              disabled={obteniendoCae === factura.nroFormulario}
                              sx={btnSx('#2196f3', '#1976d2')}
                            >
                              {obteniendoCae === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <CloudDownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {(() => {
                          const remitoBtn = getRemitoButtonState(factura);
                          if (!remitoBtn.show) return null;
                          return (
                          <Tooltip title={remitoBtn.title}>
                            <IconButton
                              onMouseEnter={() => { void getRemitoStatus(factura.nroFormulario); }}
                              onClick={(e) => openRemitoActions(e, factura.nroFormulario)}
                              size="small"
                              disabled={generandoRemito === factura.nroFormulario}
                              sx={btnSx(remitoBtn.bg, remitoBtn.hover)}
                            >
                              {generandoRemito === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <LocalShippingIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          );
                        })()}
                        {facturaPermiteImpresion(factura) && (
                        <Tooltip title="Imprimir">
                          <IconButton
                            onClick={(e) => handleImprimirFactura(e, factura)}
                            size="small"
                            disabled={printingFacturaId === factura.nroFormulario || facturaPrinter.printing}
                            sx={btnSx('#2196f3', '#1976d2')}
                          >
                            {printingFacturaId === factura.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        )}
                        <Tooltip title="Más acciones">
                          <IconButton
                            size="small"
                            onClick={(e) => handleKebabOpen(e, factura.nroFormulario)}
                            sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
              {loading && facturas.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && facturas.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay facturas para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabFactura && renderKebabFacturaItems(currentKebabFactura)}
      </Menu>

      <Menu
        anchorEl={remitoActionsAnchor?.el}
        open={Boolean(remitoActionsAnchor)}
        onClose={closeRemitoActions}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            const facturaId = remitoActionsAnchor?.facturaId;
            closeRemitoActions();
            if (facturaId) void handleGenerarRemito(facturaId);
          }}
        >
          <ListItemIcon><LocalShippingIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Generar remito total</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const facturaId = remitoActionsAnchor?.facturaId;
            closeRemitoActions();
            if (facturaId) void handleAbrirRemitoParcial(facturaId);
          }}
        >
          <ListItemIcon><LocalShippingIcon fontSize="small" sx={{ color: '#f57c00' }} /></ListItemIcon>
          <ListItemText>Generar remito parcial</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={printMenuAnchor?.el}
        open={Boolean(printMenuAnchor)}
        onClose={closePrintMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            const factura = printMenuAnchor?.factura;
            if (factura) void ejecutarImpresionFactura(factura, 'pdf');
          }}
        >
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Imprimir PDF</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const factura = printMenuAnchor?.factura;
            if (factura) void ejecutarImpresionFactura(factura, 'ticket');
          }}
        >
          <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Imprimir ticket</ListItemText>
        </MenuItem>
      </Menu>

      {facturaPrinter.pdfFactura && (
        <PDFViewer
          pdfUrl={facturaPrinter.pdfFactura.url}
          onClose={facturaPrinter.closePdfFactura}
          title={`Factura ${facturaPrinter.pdfFactura.nroFormulario}`}
          documentId={`${facturaPrinter.pdfFactura.nroFormulario}`}
          documentType="factura"
          downloadFileName={facturaPrinter.pdfFactura.fileName ?? undefined}
        />
      )}

      {qrDialogOpen && selectedFacturaForPayment && (
        <QRPaymentDialog
          open={qrDialogOpen}
          onClose={handleCloseQRDialog}
          qrCode={qrCode}
          paymentLink={paymentLink}
          facturaNumero={selectedFacturaForPayment}
          loading={qrLoading}
          isPrototype={paymentLinkIsPrototype}
        />
      )}

      <EnviarFacturaEmailDialog
        open={emailDialogOpen}
        facturaNumero={selectedFacturaForEmail}
        preview={emailPreview}
        loading={emailPreviewLoading}
        sending={emailSending}
        onClose={handleCloseEmailDialog}
        onSend={(payload) => { void handleEnviarFacturaEmail(payload); }}
        onValidationError={(msg) => enqueueSnackbar(msg, { variant: 'warning' })}
      />

      <EditarObservacionesFacturaDialog
        open={observacionesDialogOpen}
        facturaNumero={selectedFacturaForObservaciones}
        comprobanteLabel={observacionesComprobanteLabel}
        observacionesIniciales={observacionesIniciales}
        loading={observacionesLoading}
        saving={observacionesSaving}
        onClose={handleCloseObservacionesDialog}
        onSave={(observaciones) => { void handleGuardarObservaciones(observaciones); }}
      />
    </Box>
  );
};

export default Facturas;

